import { getOtherMethod } from 'components/EditOtherForm/EditOtherCommon';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimContractOtherCostViewQuery,
    BimWorkOrderOtherDataViewQuery,
    UerContractOtherCostViewQuery,
    LimitDistributionMethodEnum,
    Scalars,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import { useEffect, useMemo } from 'react';

import { CalculationMethodMap, ChapterMap } from '../constants';
import {
    bimContractOtherCostViewGql,
    bimWorkOrderChangeOtherDataViewGql,
    bimWorkOrderOtherDataViewGql,
    bimWorkOrderVersionOtherDataGql,
    uerContractOtherCostViewGql,
} from '../otherViewQueries';

const mapDataToView = (
    data:
        | BimContractOtherCostViewQuery['bimContractOtherCost']['data']
        | BimWorkOrderOtherDataViewQuery['bimWorkOrderOtherData']['data']
        | UerContractOtherCostViewQuery['uerContractOtherCostForUpdate']['data'],
): {
    creationMethod: string;
    name: string | undefined;
    calculationMethod: string | undefined;
    isCalculatingMethodPercent: boolean;
    value: string | null;
    chapter: string;
    formula: string | null | undefined;
    otherCostTypeName: string | undefined;
    key?: string | null;
} => ({
    creationMethod: getOtherMethod({
        status: data?.status,
        isGlobal: data?.isGlobalOtherCost,
    }),
    name: data?.name,
    calculationMethod: data?.calculatingType ? CalculationMethodMap[data.calculatingType] : undefined,
    isCalculatingMethodPercent: data?.calculatingType === OtherCostCalculationTypeEnum.Percent,
    value: data?.calculatingTypeValue ? formatNumberToRu(Number(data.calculatingTypeValue), 3) : null,
    chapter: data?.chapter ? ChapterMap[data.chapter] : 'Лимиты заказчика',
    formula: data && 'formula' in data ? data.formula : null,
    otherCostTypeName: data && 'catalogOtherCostType' in data ? data.catalogOtherCostType?.name : undefined,
    key: data && 'key' in data ? data.key : undefined,
});

interface IUseOtherData {
    contractId?: number;
    contractOtherCostId?: number;
    methodology: ProjectMethodologyEnum;
    workOrderOtherId?: number;
    workOrderChangeOtherId?: number;
    isVersionPage?: boolean;
    isWorkOrderChange?: boolean;
}

export const useOtherData = ({
    contractId,
    contractOtherCostId,
    methodology,
    workOrderOtherId,
    workOrderChangeOtherId,
    isVersionPage,
    isWorkOrderChange,
}: IUseOtherData) => {
    const memoizedRequestIds = useMemo(
        () => [
            'bimContractOtherCostView',
            'bimWorkOrderOtherDataView',
            'uerContractOtherCostView',
            'bimWorkOrderVersionOtherDataView',
            'bimWorkOrderChangeOtherDataView',
        ],
        [],
    );

    const [resetViewData] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            resetViewData();
        },
        [resetViewData],
    );

    const contractVariables = useMemo(
        () => ({
            contractId,
            contractOtherCostId,
        }),
        [contractOtherCostId, contractId],
    );

    const workOrderVariables = useMemo(
        () => ({
            workOrderOtherPriceId: workOrderOtherId,
        }),
        [workOrderOtherId],
    );

    const workOrderChangeVariables = useMemo(
        () => ({
            workOrderChangeOtherPriceId: workOrderChangeOtherId,
            workOrderOtherPriceId: workOrderOtherId,
        }),
        [workOrderChangeOtherId, workOrderOtherId],
    );

    const workOrderVersionVariables = useMemo(
        () => ({
            workOrderVersionOtherPriceId: workOrderOtherId,
        }),
        [workOrderOtherId],
    );

    const [bimContractOtherCostData, bimContractOtherCostStatus] = useRequest({
        requestId: 'bimContractOtherCostView',
        variables: contractVariables,
        query: bimContractOtherCostViewGql,
        withEffect: !!(contractId && contractOtherCostId && methodology === ProjectMethodologyEnum.Bim),
    });

    const [uerContractOtherCostData, uerContractOtherCostStatus] = useRequest({
        requestId: 'uerContractOtherCostView',
        variables: contractVariables,
        query: uerContractOtherCostViewGql,
        withEffect: !!(contractId && contractOtherCostId && methodology === ProjectMethodologyEnum.Uer),
    });

    const [bimWorkOrderOtherDataData, bimWorkOrderOtherStatus] = useRequest({
        requestId: 'bimWorkOrderOtherDataView',
        query: bimWorkOrderOtherDataViewGql,
        variables: workOrderVariables,
        withEffect: !!(
            workOrderOtherId &&
            methodology === ProjectMethodologyEnum.Bim &&
            !isVersionPage &&
            !isWorkOrderChange
        ),
    });

    const [bimWorkOrderChangeOtherDataData, bimWorkOrderChangeOtherStatus] = useRequest({
        requestId: 'bimWorkOrderChangeOtherDataView',
        query: bimWorkOrderChangeOtherDataViewGql,
        variables: workOrderChangeVariables,
        withEffect: !!(isWorkOrderChange && methodology === ProjectMethodologyEnum.Bim && !isVersionPage),
    });

    const [bimWorkOrderVersionOtherDataData, bimWorkOrderVersionOtherStatus] = useRequest({
        requestId: 'bimWorkOrderVersionOtherDataView',
        query: bimWorkOrderVersionOtherDataGql,
        variables: workOrderVersionVariables,
        withEffect: !!(workOrderOtherId && methodology === ProjectMethodologyEnum.Bim && isVersionPage),
    });

    const currentData = isVersionPage
        ? bimWorkOrderVersionOtherDataData?.bimWorkOrderVersionOtherData?.data
        : bimWorkOrderOtherDataData?.bimWorkOrderOtherData?.data;

    const currentStatus = isVersionPage ? bimWorkOrderVersionOtherStatus : bimWorkOrderOtherStatus;

    const data = useMemo(() => {
        if (methodology === ProjectMethodologyEnum.Bim) {
            if (isWorkOrderChange) {
                return mapDataToView(bimWorkOrderChangeOtherDataData?.bimWorkOrderChangeOtherData?.data);
            }

            return mapDataToView(workOrderOtherId ? currentData : bimContractOtherCostData?.bimContractOtherCost?.data);
        }

        return mapDataToView(uerContractOtherCostData?.uerContractOtherCostForUpdate?.data);
    }, [
        bimContractOtherCostData?.bimContractOtherCost?.data,
        bimWorkOrderChangeOtherDataData?.bimWorkOrderChangeOtherData?.data,
        currentData,
        isWorkOrderChange,
        methodology,
        uerContractOtherCostData?.uerContractOtherCostForUpdate?.data,
        workOrderOtherId,
    ]);

    const otherCostLimits = useMemo<
        | {
              cost: Scalars['Decimal'];
              constructionType: { id: number };
              distributionMethod?: LimitDistributionMethodEnum;
          }[]
        | undefined
    >(() => {
        if (methodology === ProjectMethodologyEnum.Bim) {
            if (isWorkOrderChange) {
                return bimWorkOrderChangeOtherDataData?.bimWorkOrderChangeOtherData?.data?.otherCostLimits;
            }

            return workOrderOtherId
                ? currentData?.otherCostLimits
                : bimContractOtherCostData?.bimContractOtherCost?.data?.otherCostLimits;
        }

        return uerContractOtherCostData?.uerContractOtherCostForUpdate?.data?.groupConstructionLimits;
    }, [
        bimContractOtherCostData?.bimContractOtherCost?.data?.otherCostLimits,
        bimWorkOrderChangeOtherDataData?.bimWorkOrderChangeOtherData?.data?.otherCostLimits,
        currentData?.otherCostLimits,
        isWorkOrderChange,
        methodology,
        uerContractOtherCostData?.uerContractOtherCostForUpdate?.data?.groupConstructionLimits,
        workOrderOtherId,
    ]);

    const manualLimitValues = useMemo(() => {
        if (methodology === ProjectMethodologyEnum.Uer) {
            return uerContractOtherCostData?.uerContractOtherCostForUpdate?.data?.limits;
        }

        return undefined;
    }, [methodology, uerContractOtherCostData?.uerContractOtherCostForUpdate?.data?.limits]);

    return useMemo(
        () => ({
            data,
            otherCostLimits: otherCostLimits ?? [],
            manualLimitValues,
            contractOtherStatus:
                methodology === ProjectMethodologyEnum.Bim ? bimContractOtherCostStatus : uerContractOtherCostStatus,
            workOrderOtherStatus: currentStatus,
            getWorkOrderChangeStatus: bimWorkOrderChangeOtherStatus,
        }),
        [
            bimContractOtherCostStatus,
            bimWorkOrderChangeOtherStatus,
            currentStatus,
            data,
            manualLimitValues,
            methodology,
            otherCostLimits,
            uerContractOtherCostStatus,
        ],
    );
};
